import React, { useEffect, useState } from "react";
import "./AppHeader.scss";
import {
  theme,
  Layout,
  Typography,
  Button,
  Avatar,
  Badge,
  notification,
} from "antd";
import {
  UserOutlined,
  HeartOutlined,
  LogoutOutlined,
  ShoppingCartOutlined,
  StarOutlined,
  HistoryOutlined,
  BellOutlined,
} from "@ant-design/icons";
import useWindowWidth from "../../hooks/useWindowWidth";
import { useLocation, useNavigate } from "react-router";
import { Login, Register, ForgetPassword, ConfirmModal } from "../../modals";
import { useSelector } from "react-redux";
import { logOut, resetAuthError } from "../../features/auth/authSlice";
import { useDispatch } from "react-redux";
import { signUp } from "../../features/auth/authActions";
import { HeaderDrawer, MainTitle } from "./components";
import RolesHelper from "../../common/services/permissions/RolesHelper";
import useNotifications from "../../hooks/useNotifications";
import NotificationPanel from "../NotificationPanel/NotificationPanel";
import Utils from "../../utils";
const { Header } = Layout;

export default function AppHeader() {
  const { useToken } = theme;
  const { token } = useToken();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isMobile } = useWindowWidth();
  const location = useLocation();
  const { isAuthenticated, error } = useSelector((state) => state?.auth);
  const { items } = useSelector((state) => state?.card);
  const [isOpened, setIsOpened] = useState(false);
  const [loginOpened, setOpenLogin] = useState(
    pathname === "/login" && !isAuthenticated ? true : false
  );
  const [registerOpened, setOpenRegister] = useState(false);
  const [isForgetPasswordOened, setIsFrogetPasswordOpened] = useState(false);
  const [isLogOutModalOpened, setIsLogOutModalOpened] = useState(false);
  const pageVisible = RolesHelper?.getPagesVisibility();
  const [api, contextHolder] = notification.useNotification();
  // The bell: badge count, drawer state and click handlers. Only a
  // logged-in customer has a feed (guests get 403), so nothing is fetched
  // until then.
  const notifications = useNotifications({
    enabled: Boolean(isAuthenticated),
    onError: (err) => {
      // `status` is bookkeeping for the slice, not copy for the customer.
      const { status, ...rest } = err && typeof err === "object" ? err : {};
      api.error({
        title: "Notifications",
        description:
          Utils.getErrorText(rest?.detail || rest) || "Something went wrong.",
        placement: "top",
      });
    },
  });

  const onTitleClick = () => {
    navigate("/");
  };

  const handleLogOut = () => {
    dispatch(logOut());
    navigate("/");
    setIsLogOutModalOpened(false);
  };

  const calCulateOrderItemCount = (arr) => {
    let sum = 0;
    arr.forEach((el) => (sum += el?.quantity));
    return sum;
  };

  const orderItemCount = calCulateOrderItemCount(items);

  // The bell, same shape as the cart button beside it: a Badge only while
  // there is something to count, so the plain button lines up with the
  // other icons. Only a logged-in customer has a feed, so a guest gets no
  // bell at all.
  const renderBellBtn = () => {
    if (!isAuthenticated) {
      return null;
    }
    const bellBtn = (
      <Button
        aria-label="Notifications"
        icon={
          <BellOutlined
            style={{
              color: notifications?.drawerOpen
                ? token?.colorPrimary
                : token?.icons?.whiteIcon,
              fontSize: "24px",
            }}
          />
        }
        type="text"
        onClick={() => {
          setIsOpened(false);
          notifications?.openDrawer();
        }}
      />
    );
    if (notifications?.unreadCount) {
      return (
        <Badge
          count={notifications?.unreadCount}
          size="small"
          overflowCount={99}
          // On mobile the bell is the header's last item, flush with the
          // screen edge: pull the count inward so it is never cut off.
          offset={isMobile ? [-8, 3] : undefined}
          color={token?.colorPrimary}>
          {bellBtn}
        </Badge>
      );
    }
    return bellBtn;
  };

  useEffect(() => {
    if (pathname === "/login" && !isAuthenticated) {
      setOpenLogin(true);
    }
  }, [pathname, isAuthenticated]);

  return (
    <Header
      className="appHeader-cont"
      style={{
        borderBottom: `1px solid ${token?.colorPrimary}`,
      }}>
      {contextHolder}
      {isAuthenticated ? (
        <NotificationPanel
          open={notifications?.drawerOpen}
          onClose={notifications?.closeDrawer}
          notifications={notifications?.notifications}
          loading={notifications?.loading}
          totalCount={notifications?.totalCount}
          perPage={notifications?.perPage}
          currentPage={notifications?.currentPage}
          onPageChange={notifications?.handlePageChange}
          onNotificationClick={notifications?.handleNotificationClick}
          onMarkAllRead={notifications?.handleMarkAllRead}
          markAllLoading={notifications?.markAllLoading}
          unreadCount={notifications?.unreadCount}
          unreadByType={notifications?.unreadByType}
          onlyUnread={notifications?.onlyUnread}
          onToggleUnread={notifications?.handleToggleUnread}
        />
      ) : null}
      <div className="appHeader-cont__flex">
        <MainTitle
          onTitleClick={onTitleClick}
          setIsOpened={setIsOpened}
          isMobile={isMobile}
          isMenuIconVisible={true}
        />
        {isMobile && pageVisible?.is_checkout_visible ? (
          orderItemCount ? (
            <Badge
              count={orderItemCount}
              size="small"
              color={token?.colorPrimary}
              styles={{
                root: {
                  marginRight: "15px",
                },
              }}>
              <Button
                icon={
                  <ShoppingCartOutlined
                    style={{
                      color:
                        location?.pathname === "/checkout"
                          ? token?.colorPrimary
                          : token?.icons?.whiteIcon,
                      fontSize: "24px",
                    }}
                  />
                }
                type="text"
                onClick={() => {
                  setIsOpened(false);
                  navigate("checkout");
                }}
              />
            </Badge>
          ) : (
            <Button
              icon={
                <ShoppingCartOutlined
                  style={{
                    color:
                      location?.pathname === "/checkout"
                        ? token?.colorPrimary
                        : token?.icons?.whiteIcon,
                    fontSize: "24px",
                  }}
                />
              }
              type="text"
              onClick={() => {
                setIsOpened(false);
                navigate("checkout");
              }}
            />
          )
        ) : null}
        {isMobile ? renderBellBtn() : null}
        {isMobile ? (
          <HeaderDrawer
            token={token}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            setOpenLogin={setOpenLogin}
            setIsLogOutModalOpened={setIsLogOutModalOpened}
            setOpenRegister={setOpenRegister}
            isMobile={isMobile}
            pageVisible={pageVisible}
            unreadNotifications={notifications?.unreadCount}
            onOpenNotifications={notifications?.openDrawer}
          />
        ) : (
          <div className="appHeader-cont__flex__filters">
            <div
              className="appHeader-cont__flex__filters__navigationBar"
              style={orderItemCount >= 10 ? { gap: "16px" } : null}>
              <div className="appHeader-cont__flex__filters__navigationBar__btnsWrapper">
                {isAuthenticated && pageVisible?.is_orders_visible ? (
                  <Button
                    aria-label="Order history"
                    icon={
                      <HistoryOutlined
                        style={{
                          color:
                            location?.pathname === "/orders"
                              ? token?.colorPrimary
                              : token?.icons?.whiteIcon,
                          fontSize: "24px",
                        }}
                      />
                    }
                    type="text"
                    onClick={() => {
                      setIsOpened(false);
                      navigate("orders");
                    }}
                  />
                ) : null}
                {isAuthenticated && pageVisible?.is_reviews_visible ? (
                  <Button
                    icon={
                      <StarOutlined
                        style={{
                          color:
                            location?.pathname === "/reviews"
                              ? token?.colorPrimary
                              : token?.icons?.whiteIcon,
                          fontSize: "24px",
                        }}
                      />
                    }
                    type="text"
                    onClick={() => {
                      setIsOpened(false);
                      navigate("reviews");
                    }}
                  />
                ) : null}
                {isAuthenticated && pageVisible?.is_favorites_visible ? (
                  <Button
                    icon={
                      <HeartOutlined
                        style={{
                          color:
                            location?.pathname === "/favorites"
                              ? token?.colorPrimary
                              : token?.icons?.whiteIcon,
                          fontSize: "24px",
                        }}
                      />
                    }
                    type="text"
                    onClick={() => {
                      setIsOpened(false);
                      navigate("favorites");
                    }}
                  />
                ) : null}
                {pageVisible?.is_checkout_visible ? (
                  orderItemCount ? (
                    <Badge
                      count={orderItemCount}
                      size="small"
                      color={token?.colorPrimary}>
                      <Button
                        icon={
                          <ShoppingCartOutlined
                            style={{
                              color:
                                location?.pathname === "/checkout"
                                  ? token?.colorPrimary
                                  : token?.icons?.whiteIcon,
                              fontSize: "24px",
                            }}
                          />
                        }
                        type="text"
                        onClick={() => {
                          setIsOpened(false);
                          navigate("checkout");
                        }}
                      />
                    </Badge>
                  ) : (
                    <Button
                      icon={
                        <ShoppingCartOutlined
                          style={{
                            color:
                              location?.pathname === "/checkout"
                                ? token?.colorPrimary
                                : token?.icons?.whiteIcon,
                            fontSize: "24px",
                          }}
                        />
                      }
                      type="text"
                      onClick={() => {
                        setIsOpened(false);
                        navigate("checkout");
                      }}
                    />
                  )
                ) : null}
                {renderBellBtn()}
              </div>
              {!isAuthenticated ? (
                <>
                  <div className="appHeader-cont__flex__filters__navigationBar__authWrapper">
                    <Button
                      type="text"
                      onClick={() => {
                        setOpenLogin(true);
                        if (error) {
                          dispatch(resetAuthError());
                        }
                      }}>
                      <Typography.Text
                        style={{
                          color: token?.Typography?.colorPrimaryText,
                        }}>
                        Log in
                      </Typography.Text>
                    </Button>
                    <Button
                      onClick={() => {
                        setOpenRegister(true);
                        if (error) {
                          dispatch(resetAuthError());
                        }
                      }}
                      type="primary">
                      Sign Up
                    </Button>
                  </div>
                </>
              ) : (
                <div
                  className="appHeader-cont__flex__filters__navigationBar__authWrapper"
                  style={{
                    alignItems: "center",
                  }}>
                  {pageVisible?.is_profile_visible ? (
                    <Badge
                      size="small"
                      dot
                      styles={{
                        root: {
                          cursor: "pointer",
                        },
                        indicator: {
                          background: token?.colorPrimary,
                        },
                      }}
                      onClick={() => {
                        navigate("profile");
                      }}>
                      <Avatar
                        shape="square"
                        style={{
                          backgroundColor: token?.Card?.colorBgCard,
                        }}
                        size={24}
                        icon={
                          <UserOutlined
                            style={{
                              color:
                                location?.pathname === "/profile"
                                  ? token?.colorPrimary
                                  : token?.icons?.whiteIcon,
                              fontSize: "18px",
                            }}
                          />
                        }
                      />
                    </Badge>
                  ) : null}
                  <Button type="text">
                    <Typography.Text
                      className="appHeader-cont__flex__drawer__body__text"
                      style={{
                        color: token?.colorError,
                      }}
                      onClick={() => {
                        setIsLogOutModalOpened(true);
                      }}>
                      Log Out
                    </Typography.Text>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Login
        isOpened={loginOpened}
        setIsOpened={setOpenLogin}
        setIsRegisterOpened={setOpenRegister}
        setIsFrogetPasswordOpened={setIsFrogetPasswordOpened}
        title={"Login"}
        token={token}
      />
      <Register
        isOpened={registerOpened}
        title="Sign Up"
        onCancel={() => {
          setOpenRegister(false);
        }}
        onOk={(values) => {
          dispatch(signUp(values))?.then((res) => {
            if (res?.type === "sign_up/fulfilled") {
              setTimeout(() => {
                setOpenLogin(true);
              }, 500);
            }
          });
        }}
        token={token}
      />
      <ForgetPassword
        title={"Forgot Password"}
        isOpened={isForgetPasswordOened}
        onCancel={() => {
          setIsFrogetPasswordOpened(false);
        }}
        openLogedInModal={setOpenLogin}
      />
      <ConfirmModal
        isOpened={isLogOutModalOpened}
        title={"Are you sure"}
        subTitle={"You want to log out ?"}
        icon={
          <LogoutOutlined
            style={{ fontSize: "24px", transform: "rotate(180deg)" }}
          />
        }
        onCancel={() => {
          setIsLogOutModalOpened(false);
        }}
        onOk={handleLogOut}
        token={token}
      />
    </Header>
  );
}
